import SpeakButton from "../EnglishTool/SpeakButton";
import { getSentencePool } from "../EnglishTool/pool/sentencePool";
import { getReadingPool } from "../EnglishTool/pool/readingPool";

export const chapterPages = {
    1: [
      {
        title: <h2>Academic Noun Clauses, Nominalization & Appositive Complexity</h2>,
        body: (
          <>
            <p>
              <b>Advanced Noun Clauses:</b> In critical writing, noun clauses support thesis statements, integrate counterclaims, and embed expert perspectives.<br />
              <b>Subject:</b> <i><b>That artificial intelligence will disrupt every industry</b> is the driving assumption behind modern innovation policy.</i>
              <SpeakButton text="That artificial intelligence will disrupt every industry is the driving assumption behind modern innovation policy." /><br />
              <b>Object:</b> <i>The researcher investigated <b>whether cognitive bias influenced the results</b>.</i>
              <SpeakButton text="The researcher investigated whether cognitive bias influenced the results." />
            </p>
            <p>
              <b>Nominalization for Objectivity:</b> Transform actions/adjectives into abstract nouns to create academic tone.<br />
              <i>"People responded quickly." → "The <b>responsiveness</b> of participants accelerated the study."</i>
              <SpeakButton text="The responsiveness of participants accelerated the study." />
              <br />
              <b>Challenge:</b> Turn "They succeeded easily." into a sentence with nominalization.
            </p>
            <p>
              <b>Appositive Phrases for Evidence:</b> Use appositives to introduce credentials, sources, or additional data.<br />
              <i>Her conclusion, <b>a synthesis of over twenty studies</b>, was widely cited.</i>
              <SpeakButton text="Her conclusion, a synthesis of over twenty studies, was widely cited." />
              <br />
              <b>Try it:</b> Spot the appositive: "The CEO, <b>a respected innovator</b>, addressed the board."
            </p>
            <p>
              <b>Critical Reading:</b>
              <br />
              <i>{getReadingPool(10)[0]?.statement}</i>
              <br />
              <b>Task:</b> What is the role of each grammatical feature in the author’s argument?
            </p>
          </>
        ),
      },
      {
        title: <h2>Theme, Motif & Extended Metaphor in Critical Analysis</h2>,
        body: (
          <>
            <p>
              <b>Theme:</b> At this level, you’ll extract abstract, multi-faceted themes—often unstated.<br />
              <b>Motif:</b> Sophisticated analysis requires tracing subtle, recurring imagery across genres or media.<br />
              <b>Example:</b> <i>The recurring motif of "mirrors" in the novel highlights the elusive nature of identity.</i>
              <SpeakButton text="The recurring motif of mirrors in the novel highlights the elusive nature of identity." />
            </p>
            <p>
              <b>Extended Metaphor Across Arguments:</b> Essays may sustain a metaphor over multiple paragraphs.<br />
              <i>
                "Globalization is painted as a double-edged sword, both bridge and barrier in the author’s economic argument."
              </i>
              <SpeakButton text="Globalization is painted as a double-edged sword, both bridge and barrier in the author’s economic argument." />
            </p>
            <p>
              <b>Critical Challenge:</b> Identify motif and theme in:<br />
              <i>
                "Every page is a new threshold, every chapter a test of endurance."
              </i>
              <SpeakButton text="Every page is a new threshold, every chapter a test of endurance." />
            </p>
            <p>
              <b>Task:</b> Write a 2–3 sentence analysis of a metaphor in an editorial, speech, or research article you’ve read.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Integrated Academic Sentences</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Find and explain the noun clause, appositive, and any nominalization in:<br />
              "That trust in institutions is eroding, a conclusion drawn by numerous scholars, underscores the urgency of reform."
              <SpeakButton text="That trust in institutions is eroding, a conclusion drawn by numerous scholars, underscores the urgency of reform." />
            </p>
            <p>
              <b>Task 2:</b> Compose your own topic sentence on climate change, using a noun clause, a nominalization, and an appositive.
              <br />
              <i>Example: "How society addresses rising emissions, a central concern for policymakers, will shape future sustainability."</i>
              <SpeakButton text="How society addresses rising emissions, a central concern for policymakers, will shape future sustainability." />
            </p>
            <p>
              <b>Challenge:</b> Write a thesis statement for a literary analysis using both a noun clause and extended metaphor.
            </p>
          </>
        ),
      },
    ],
  
    2: [
      {
        title: <h2>Voice, Modality & Evidence in Academic Argument</h2>,
        body: (
          <>
            <p>
              <b>Active/Passive Voice in Evidence:</b> Choose strategically for focus and clarity.<br />
              <i>"Scholars have critiqued the methodology." (active)</i>
              <SpeakButton text="Scholars have critiqued the methodology." />
              <i>"The methodology has been critiqued by scholars." (passive)</i>
              <SpeakButton text="The methodology has been critiqued by scholars." />
            </p>
            <p>
              <b>Modal Verbs for Analysis:</b> Use "might," "must," "should," etc., to evaluate, qualify, or hedge claims.<br />
              <i>"The policy may represent a turning point."</i>
              <SpeakButton text="The policy may represent a turning point." />
              <b>Try it:</b> Rewrite "Scientists prove the theory" with appropriate academic modality.
            </p>
            <p>
              <b>Subjunctive & Counterfactuals:</b> Practice hypothetical reasoning.<br />
              <i>"If the author’s claim were substantiated, the implications would be profound."</i>
              <SpeakButton text="If the author’s claim were substantiated, the implications would be profound." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Logical Structure, Fragments & Argument Flow</h2>,
        body: (
          <>
            <p>
              <b>Compound-Complex Structure:</b> Use layered clauses for nuanced, logically connected arguments.<br />
              <i>
                "Although the study presents compelling data, it lacks longitudinal analysis, and the conclusions remain tentative."
              </i>
              <SpeakButton text="Although the study presents compelling data, it lacks longitudinal analysis, and the conclusions remain tentative." />
            </p>
            <p>
              <b>Editing Academic Run-Ons & Fragments:</b> Identify, correct, and explain why they undermine scholarly writing.<br />
              <i>Run-on: "The survey included 2,000 students it spanned five countries."</i>
              <SpeakButton text="The survey included 2,000 students it spanned five countries." />
              <i>Fragment: "Despite significant results."</i>
              <SpeakButton text="Despite significant results." />
            </p>
            <p>
              <b>Challenge:</b> Write an original, compound-complex sentence on technology or society, using at least one modal and one passive construction.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Advanced Verb Structures</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Identify all verbs (main, modal, and passive):<br />
              <i>{getSentencePool(10).find(s => s.focus === "verbs")?.sentence}</i>
            </p>
            <p>
              <b>Task 2:</b> Convert to passive with appropriate modality:<br />
              <i>"Analysts forecast a dramatic market shift."</i>
              <SpeakButton text="Analysts forecast a dramatic market shift." />
            </p>
            <p>
              <b>Challenge:</b> Write an analytical sentence for a research paper, blending modality and complex clause structure.
            </p>
          </>
        ),
      },
    ],
  
    3: [
      {
        title: <h2>Discipline-Specific Adjectives & Academic Tone</h2>,
        body: (
          <>
            <p>
              <b>Precision in Adjectives:</b> Advanced arguments depend on nuanced, domain-specific adjectives.<br />
              <i>"The <b>methodologically rigorous</b> analysis provided <b>incontrovertible</b> evidence."</i>
              <SpeakButton text="The methodologically rigorous analysis provided incontrovertible evidence." />
            </p>
            <p>
              <b>Evaluative/Comparative Adjectives in Critique:</b> Use adjectives to compare research, theories, or literary works.<br />
              <i>"This framework is more adaptable than earlier models and the most influential in recent scholarship."</i>
              <SpeakButton text="This framework is more adaptable than earlier models and the most influential in recent scholarship." />
            </p>
            <p>
              <b>Figurative Adjectives in Analysis:</b> Support arguments with metaphorical or symbolic adjectives.<br />
              <i>"The debate generated a <b>stormy</b> exchange and <b>heated</b> disagreement."</i>
              <SpeakButton text="The debate generated a stormy exchange and heated disagreement." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Superlative, Comparative & Critical Synthesis</h2>,
        body: (
          <>
            <p>
              <b>Evaluative Synthesis:</b> "Most robust," "least persuasive," "more nuanced"—critical writing uses these to synthesize sources and arguments.<br />
              <i>"Her method proved more accurate than that of previous studies, yet perhaps less generalizable."</i>
              <SpeakButton text="Her method proved more accurate than that of previous studies, yet perhaps less generalizable." />
            </p>
            <p>
              <b>Superlatives in Scholarly Context:</b> Use with caution; always support with evidence.<br />
              <i>"This remains the most cited article on climate ethics."</i>
              <SpeakButton text="This remains the most cited article on climate ethics." />
            </p>
            <p>
              <b>Challenge:</b> Write a paragraph synthesizing two or more sources, using comparative/superlative adjectives and at least one metaphor.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Analytical Writing & Figurative Critique</h2>,
        body: (
          <>
            <p>
              <b>Task:</b> Analyze a recent news story or research article, identifying at least three advanced adjectives and one figure of speech.
            </p>
            <p>
              <b>Find all adjectives and figurative elements:</b><br />
              <i>{getSentencePool(10).filter(s => s.focus === "adjectives")[1]?.sentence}</i>
            </p>
            <p>
              <b>Extension:</b> How do adjectives and metaphorical language enhance or bias the author's point?
            </p>
          </>
        ),
      },
    ],
  
    4: [
      {
        title: <h2>Adverbs, Cohesion & Rhetorical Force</h2>,
        body: (
          <>
            <p>
              <b>Adverbials for Argument Structure:</b> Use advanced adverbs and adverbial phrases to signal logic, emphasis, or counterargument.<br />
              <i>"Consequently, the hypothesis was rejected despite initial support."</i>
              <SpeakButton text="Consequently, the hypothesis was rejected despite initial support." />
            </p>
            <p>
              <b>Transitions for Critical Clarity:</b> Cohesive arguments depend on adverbial transitions ("moreover," "however," "similarly," "in contrast").<br />
              <i>"Alternatively, one could interpret the data as inconclusive."</i>
              <SpeakButton text="Alternatively, one could interpret the data as inconclusive." />
            </p>
            <p>
              <b>Rhetorical Adverbs in Persuasion & Media:</b> Show stance, uncertainty, or critique.<br />
              <i>"Purportedly, these reforms benefit the middle class, but critics remain skeptical."</i>
              <SpeakButton text="Purportedly, these reforms benefit the middle class, but critics remain skeptical." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Critical Adverbial Mastery & Media Analysis</h2>,
        body: (
          <>
            <p>
              <b>Intensifiers, Hedges & Nuance:</b> "Undoubtedly," "tentatively," "ostensibly," "arguably" refine academic claims.<br />
              <i>"Arguably, the court’s decision reflects a shift in social values."</i>
              <SpeakButton text="Arguably, the court’s decision reflects a shift in social values." />
            </p>
            <p>
              <b>Find and Explain the Role of Adverbs:</b><br />
              <i>{getSentencePool(10).find(s => s.focus === "adverbs")?.sentence}</i>
            </p>
            <p>
              <b>Media Critique:</b> How do adverbs and transitions guide readers’ interpretations in news or opinion pieces?
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Advanced Argumentation & Rhetorical Writing</h2>,
        body: (
          <>
            <p>
              <b>Compose:</b> An argumentative paragraph on a current issue, using at least three advanced adverbs, one intensifier, and two logical transitions.
            </p>
            <p>
              <b>Find all adverbs, transitions, and their rhetorical effects:</b><br />
              <i>{getSentencePool(10).filter(s => s.focus === "adverbs")[1]?.sentence}</i>
            </p>
            <p>
              <b>Challenge:</b> Explain how your use of adverbs and transitions strengthens your argument or analysis.
            </p>
          </>
        ),
      },
    ]
  };